import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { Search } from 'lucide-react';

export const AdminWalletsPage = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    adminAPI.getAllWallets(0, 500)
      .then(res => setWallets(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = wallets.filter(w =>
    !search ||
    w.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    w.user_username?.toLowerCase().includes(search.toLowerCase()) ||
    w.currency?.toLowerCase().includes(search.toLowerCase())
  );

  const totalsByCurrency = wallets.reduce((acc, w) => {
    acc[w.currency] = (acc[w.currency] || 0) + w.balance;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-wallets-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>All Wallets</h1>
          <p className="mt-2 text-muted-foreground">View all user wallets across the platform</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Object.entries(totalsByCurrency).map(([currency, total]) => (
            <Card key={currency}>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total {currency}</p>
                <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {total.toFixed(2)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Wallets ({filtered.length})</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9 w-64"
                  placeholder="Search by email or currency..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="wallet-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Email</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(w => (
                    <TableRow key={w.id}>
                      <TableCell>{w.user_email}</TableCell>
                      <TableCell>{w.user_username || '-'}</TableCell>
                      <TableCell className="font-semibold">{w.currency}</TableCell>
                      <TableCell className="font-mono">{w.balance.toFixed(4)}</TableCell>
                      <TableCell>
                        <Badge className={w.frozen ? 'bg-red-600' : w.locked ? 'bg-yellow-600' : 'bg-green-600'}>
                          {w.frozen ? 'Frozen' : w.locked ? 'Locked' : 'Active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{formatDate(w.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
